import { signal } from '@preact/signals';
import type { Place } from '../astro/frame';
import { idbGet, idbSet } from '../lib/idb';
import { DEG } from '../lib/math';
import { fetchWithin } from '../lib/net';

// ---------- Cloud forecast (Open-Meteo, CC BY 4.0) ----------
/** One forecast hour. Every hour has a total cloud cover; anything else the forecast lacks is null, never 0. */
export interface CloudHour {
  t: number; // ms
  total: number; // %
  low: number | null;
  mid: number | null;
  high: number | null;
  visibilityKm: number | null;
  humidity: number | null;
  temp?: number | null; // °C
  feels?: number | null;
  dew?: number | null; // dew point, °C
  wind?: number | null; // m/s at 10 m
  code?: number | null; // WMO weather code
  pressure?: number | null; // surface, hPa
}

export const clouds = signal<{ hours: CloudHour[]; fetched: number; key: string; model?: string } | null>(null);

/** In and around the UK, prefer the Met Office model (2 km UKV blended with global). */
export function inUK(lat: number, lon: number) {
  return lat > 49.5 && lat < 61.5 && lon > -11 && lon < 2.5;
}

const inflight = new Map<string, Promise<CloudHour[] | null>>();
/** One request per place at a time, however many parts of the app ask. */
export function loadClouds(p: Place): Promise<CloudHour[] | null> {
  const key = `${p.lat.toFixed(2)},${p.lon.toFixed(2)}`;
  let job = inflight.get(key);
  if (!job) {
    job = fetchClouds(p, key).finally(() => inflight.delete(key));
    inflight.set(key, job);
  }
  return job;
}

/** A forecast value if it is a number in the range [lo, hi]; null if it is missing or makes no sense. */
export const num = (v: unknown, lo: number, hi: number): number | null => (typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? v : null);

/**
 * Forecast hours from Open-Meteo's hourly arrays. An hour with no total cloud cover is left out: it would
 * otherwise count as a clear sky.
 */
export function cloudHours(h: Record<string, unknown[] | undefined>): CloudHour[] {
  const at = (k: string, i: number, lo: number, hi: number) => num(h[k]?.[i], lo, hi);
  return (h.time ?? []).flatMap((t, i) => {
    const total = at('cloud_cover', i, 0, 100);
    if (total === null || typeof t !== 'number' || !Number.isFinite(t)) return [];
    const vis = at('visibility', i, 0, 1e6);
    return [{
      t: t * 1000,
      total,
      low: at('cloud_cover_low', i, 0, 100),
      mid: at('cloud_cover_mid', i, 0, 100),
      high: at('cloud_cover_high', i, 0, 100),
      visibilityKm: vis === null ? null : vis / 1000,
      humidity: at('relative_humidity_2m', i, 0, 100),
      temp: at('temperature_2m', i, -100, 100),
      feels: at('apparent_temperature', i, -120, 120),
      dew: at('dew_point_2m', i, -100, 100),
      wind: at('wind_speed_10m', i, 0, 150),
      code: at('weather_code', i, 0, 99),
      pressure: at('surface_pressure', i, 300, 1100),
    }];
  });
}

async function fetchClouds(p: Place, key: string): Promise<CloudHour[] | null> {
  const cur = clouds.peek();
  if (cur && cur.key === key && Date.now() - cur.fetched < 45 * 60e3) return cur.hours;
  const cached = await idbGet<{ hours: CloudHour[]; fetched: number; key: string }>(`clouds2:${key}`);
  // Kept by an older version, which kept hours without a cloud figure too.
  if (cached) cached.hours = cached.hours.filter((h) => num(h.total, 0, 100) !== null);
  if (cached && Date.now() - cached.fetched < 45 * 60e3) {
    clouds.value = cached;
    return cached.hours;
  }
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${p.lat.toFixed(2)}&longitude=${p.lon.toFixed(2)}` +
      '&hourly=cloud_cover,cloud_cover_low,cloud_cover_mid,cloud_cover_high,visibility,relative_humidity_2m,' +
      'temperature_2m,apparent_temperature,dew_point_2m,wind_speed_10m,weather_code,surface_pressure' +
      '&wind_speed_unit=ms&forecast_days=3&timeformat=unixtime&timezone=GMT' +
      (inUK(p.lat, p.lon) ? '&models=ukmo_seamless' : '');
    const j = await fetchWithin(url, (r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    });
    const hours = cloudHours(j.hourly);
    if (!hours.length) throw new Error('No cloud forecast'); // an older forecast beats none
    const v = { hours, fetched: Date.now(), key, model: inUK(p.lat, p.lon) ? 'Met Office' : undefined };
    clouds.value = v;
    await idbSet(`clouds2:${key}`, v);
    return hours;
  } catch {
    if (cached) {
      clouds.value = cached;
      return cached.hours;
    }
    return null;
  }
}

export function cloudAt(t: number): CloudHour | null {
  const c = clouds.peek();
  if (!c) return null;
  let best: CloudHour | null = null;
  for (const h of c.hours) if (!best || Math.abs(h.t - t) < Math.abs(best.t - t)) best = h;
  return best && Math.abs(best.t - t) < 2 * 3600e3 ? best : null;
}

// ---------- Geomagnetic activity (NOAA SWPC, public domain) ----------
export interface KpPoint {
  t: number;
  kp: number;
  kind: 'observed' | 'estimated' | 'predicted';
}

export const kp = signal<{ points: KpPoint[]; fetched: number } | null>(null);

export async function loadKp(): Promise<KpPoint[] | null> {
  const cur = kp.peek();
  if (cur && Date.now() - cur.fetched < 30 * 60e3) return cur.points;
  const cached = await idbGet<{ points: KpPoint[]; fetched: number }>('kp');
  if (cached && Date.now() - cached.fetched < 30 * 60e3) {
    kp.value = cached;
    return cached.points;
  }
  try {
    const arr = await fetchWithin('https://services.swpc.noaa.gov/products/noaa-planetary-k-index-forecast.json', (r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.json() as Promise<{ time_tag: string; kp: number; observed: string }[]>;
    });
    const points = arr
      .filter((x) => typeof x.kp === 'number')
      .map((x) => ({ t: Date.parse(x.time_tag + 'Z'), kp: x.kp, kind: x.observed as KpPoint['kind'] }));
    const v = { points, fetched: Date.now() };
    kp.value = v;
    await idbSet('kp', v);
    return points;
  } catch {
    if (cached) kp.value = cached;
    return cached?.points ?? null;
  }
}

// Dipole north geomagnetic pole (IGRF/WMM 2025 epoch, approximately 80.8° N, 72.7° W).
const POLE_LAT = 80.8 * DEG, POLE_LON = -72.7 * DEG;

export function geomagneticLatitude(lat: number, lon: number): number {
  const s = Math.sin(lat * DEG) * Math.sin(POLE_LAT) + Math.cos(lat * DEG) * Math.cos(POLE_LAT) * Math.cos(lon * DEG - POLE_LON);
  return Math.asin(s) / DEG;
}

/**
 * Kp that brings the edge of the auroral oval overhead here. NOAA SWPC ("Tips on viewing the aurora"):
 * at Kp 0 the oval's equatorward edge is near 66° geomagnetic latitude, moving about 2° towards the
 * equator per step of Kp. Aurora low on the poleward horizon can show at somewhat lower Kp.
 */
export function kpNeeded(lat: number, lon: number): number {
  const m = Math.abs(geomagneticLatitude(lat, lon));
  return Math.max(0, (66 - m) / 2);
}

export type AuroraChance = 'none' | 'camera' | 'possible' | 'likely';

/**
 * At the Kp that brings the oval's edge overhead, aurora is likely. Each Kp step short of that puts the
 * edge about 2° of latitude (220 km) further towards the pole, and aurora 110 km up stays above the
 * horizon out to about 700 km: so low on the horizon a step or so short, and only for cameras at three.
 */
export function auroraChance(kpValue: number, needed: number): AuroraChance {
  if (kpValue >= needed) return 'likely';
  if (kpValue >= needed - 1.5) return 'possible';
  if (kpValue >= needed - 3) return 'camera';
  return 'none';
}

/**
 * Geomagnetic activity right now, from NOAA's 3-hour Kp values: the one under way (measured, or NOAA's estimate
 * while it is still going), and the chance of aurora it gives where Kp `needed` brings the oval overhead. Null when
 * NOAA has no value for now.
 */
export function auroraNow(points: KpPoint[], now: number, needed: number): { kp: number; kind: KpPoint['kind']; chance: AuroraChance } | null {
  const cur = points.find((p) => p.t <= now && now < p.t + 3 * 3600e3);
  return cur ? { kp: cur.kp, kind: cur.kind, chance: auroraChance(cur.kp, needed) } : null;
}

// ---------- NOAA OVATION aurora nowcast (public domain), 30 to 90 minutes ahead ----------
// A 1° grid of the chance of aurora overhead. About 0.9 MB, so it is only fetched when aurora is
// plausible here and it is dark; it is kept as a 65 kB grid and refreshed every ten minutes.
export const ovation = signal<{ grid: Uint8Array; forecast: number; fetched: number } | null>(null);

export async function loadOvation(): Promise<void> {
  const cur = ovation.peek();
  if (cur && Date.now() - cur.fetched < 10 * 60e3) return;
  try {
    const j = await fetchWithin('https://services.swpc.noaa.gov/json/ovation_aurora_latest.json', (r) =>
      r.ok ? (r.json() as Promise<{ 'Forecast Time': string; coordinates: [number, number, number][] }>) : null,
    );
    if (!j) return;
    const grid = new Uint8Array(360 * 181);
    for (const [lon, lat, v] of j.coordinates) if (lon >= 0 && lon < 360 && lat >= -90 && lat <= 90) grid[(lat + 90) * 360 + lon] = v;
    ovation.value = { grid, forecast: Date.parse(j['Forecast Time']), fetched: Date.now() };
  } catch {
    /* offline */
  }
}

const R_EARTH = 6371;
/** Ground distance to the farthest aurora (at height h km) seen at least `elev` degrees up. */
const reach = (h: number, elev: number) => R_EARTH * (Math.acos((R_EARTH / (R_EARTH + h)) * Math.cos(elev * DEG)) - elev * DEG);

/** The OVATION chance overhead here, and the best chance anywhere visible 5° up towards the pole. */
export function auroraNearby(grid: Uint8Array, lat: number, lon: number): { overhead: number; horizon: number } {
  const at = (la: number, lo: number) => (la < -90 || la > 90 ? 0 : grid[(la + 90) * 360 + (((lo % 360) + 360) % 360)]);
  const la0 = Math.round(lat), lo0 = Math.round(lon);
  const overhead = at(la0, lo0);
  const D = reach(110, 5); // about 740 km
  const toward = lat >= 0 ? 1 : -1;
  let horizon = overhead;
  for (let d = 0; d <= 8; d++) {
    const la = la0 + toward * d;
    const span = Math.min(30, Math.ceil(D / (111 * Math.max(0.1, Math.cos(la * DEG)))));
    for (let dl = -span; dl <= span; dl++) {
      const lo = lo0 + dl;
      const p1 = lat * DEG, p2 = la * DEG, dlon = (lo - lon) * DEG;
      const dist = 2 * R_EARTH * Math.asin(Math.sqrt(Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dlon / 2) ** 2));
      if (dist <= D) horizon = Math.max(horizon, at(la, lo));
    }
  }
  return { overhead, horizon };
}
